import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { RHFNestedFormProvider } from '@openx/components-core/lib/Form/context/RHFNestedFormContext';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded';
import { PackageForm } from '@openx/components-package/components/PackageForm/PackageForm';
import type { UnionOfSectionNames } from '@openx/components-package/components/PackageForm/PackageForm.sections';
import { usePackageEditPermissions } from '@openx/components-package/utils/hooks/usePackageEditPermissions';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal';
import { OPEN_AUDIENCE_ORG_NAME, OPEN_AUDIENCE_ORG_UID } from '@openx/utils/services/config';
import cloneDeep from 'lodash/cloneDeep';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useDealContext } from '../../../../utils/context/DealContext';
import { useDealFormContext } from '../../../DealForm.context';
import { CuratedPackageModeSwitch } from './SelectablePackage/components/CuratedPackageModeSwitch';
import { SelectedPackageDetails } from './SelectablePackage/components/SelectedPackageDetails';
import { PackageSelect } from './SelectablePackage/PackageSelect';

type CuratedTargetingPackageTabProps = {
  sessionAccountName?: string;
  currency: string;
};

type TargetingMode = 'targeting' | 'template';

export const CuratedTargetingPackageTab = ({ sessionAccountName, currency }: CuratedTargetingPackageTabProps) => {
  const { t } = useTranslation();

  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const navigate = useNavigate();
  const location = useLocation();
  const { isOa } = useDealContext();
  const formMethods = useFormContext<DealFormDataWithTargeting>();
  const { pckg, pckgClear, pckgInitialValues, pckgIsViaInteraction, pckgIsLoading, pckgReset } = useDealFormContext();
  const { isLocked: isPackageEditDisabled } = usePackageEditPermissions(pckg || undefined);

  const isDsp = isBefInstance && !isOa;

  const [pckgUseAs, _setPckgUseAs] = useState<TargetingMode | 'templateReadOnly'>(
    formMethods.getValues('package_uid') ? 'targeting' : isDsp ? 'templateReadOnly' : 'template'
  );

  const setPckgUseAs = useCallback(
    (nextPckgUseAs: TargetingMode | 'templateReadOnly') => {
      if (pckgUseAs === nextPckgUseAs) {
        return;
      }

      if (nextPckgUseAs === 'template' || nextPckgUseAs === 'templateReadOnly') {
        formMethods.setValue(dealFormField.PACKAGE_UID, null, {
          shouldDirty: true,
        });
      }

      if (nextPckgUseAs === 'targeting') {
        formMethods.setValue(dealFormField.PACKAGE_UID, pckg?.uid ?? null, {
          shouldDirty: true,
        });
        pckgReset();
      }

      _setPckgUseAs(nextPckgUseAs);
    },
    [formMethods.setValue, pckg?.uid, pckgReset, pckgUseAs]
  );

  useEffect(() => {
    /** On initial load, targeting is handled along with deal initialValues */
    if (!pckgIsViaInteraction) {
      return;
    }

    /** Temporary work-around for DSP , where curated packages can only be used as targeting */
    if (isDsp && pckg?.uid && pckg?.is_curated) {
      formMethods.setValue(dealFormField.PACKAGE_UID, pckg?.uid, { shouldDirty: true });
    }

    if (pckg?.uid && pckg?.is_curated && pckgUseAs === 'targeting') {
      formMethods.setValue(dealFormField.PACKAGE_UID, pckg?.uid, { shouldDirty: true });
    }
  }, [pckgIsViaInteraction, isDsp, pckgUseAs, pckg?.uid, pckg?.is_curated, formMethods.setValue]);

  /** Decouple PackageForm initialValues */
  const [initialValues, setInitialValues] = useState(() => cloneDeep(pckgInitialValues));

  useEffect(() => {
    setInitialValues(cloneDeep(pckgInitialValues));
  }, [pckgInitialValues]);

  const observedTargeting = useWatch<DealFormDataWithTargeting>({ name: 'targeting' });
  const observedPackageUid = useWatch<DealFormDataWithTargeting>({ name: 'package_uid' });
  const observedAccountUid = useWatch<DealFormDataWithTargeting>({ name: 'account_uid' });

  const selectedPackageState = pckgInitialValues ?? initialValues ?? observedTargeting;

  const isSubOrg =
    (!!sessionAccountName && sessionAccountName !== OPEN_AUDIENCE_ORG_NAME) ||
    (observedAccountUid && observedAccountUid !== OPEN_AUDIENCE_ORG_UID);

  const onPackageRemove = useCallback(() => {
    pckgClear(true);
  }, [pckgClear]);

  const onPackageEdit = useCallback(() => {
    if (!pckg?.uid) {
      return;
    }

    navigate(`/inventory/packages/${pckg.uid}/edit`, {
      state: { deal: formMethods.getValues(), redirectPath: location.pathname },
    });
  }, [navigate, formMethods.getValues, pckg?.uid, location.pathname]);

  /** SubOrg can not use curated packages as targeting, force switch to template */
  useEffect(() => {
    if (isOa && isSubOrg && pckgUseAs === 'targeting') {
      setPckgUseAs('template');
    }
  }, [isOa, isSubOrg, pckgUseAs, setPckgUseAs]);

  /** Clear Error when leaving this tab */
  useEffect(() => {
    return () => {
      formMethods.clearErrors(dealFormField.PACKAGE_UID);
    };
  }, [formMethods.clearErrors]);

  /** Error for PackageUID field, prevents from submitting form when package is not selected */
  useEffect(() => {
    if (!pckg?.uid) {
      formMethods.setError(dealFormField.PACKAGE_UID, { type: 'required', message: t('Package is required') });
    } else {
      formMethods.clearErrors(dealFormField.PACKAGE_UID);
    }
  }, [pckg?.uid, formMethods.setError, formMethods.clearErrors, t]);

  useEffect(() => {
    if (isDsp && observedPackageUid && pckgUseAs === 'templateReadOnly') {
      setPckgUseAs('targeting');
    }
  }, [isDsp, observedPackageUid, pckgUseAs, setPckgUseAs]);

  const render = () => (
    <>
      {isOa &&
        (isSubOrg ? (
          <Typography
            variant="body1Bold"
            data-test="curated-package-use-as-template-label"
            sx={theme => ({
              color: theme.palette.text.secondary,
              fontSize: '13px',
              marginBottom: theme.spacing(1),
              textTransform: 'uppercase',
            })}
          >
            {t('Use as template of the following:')}
          </Typography>
        ) : (
          <CuratedPackageModeSwitch mode={pckgUseAs as TargetingMode} onModeChange={setPckgUseAs} />
        ))}

      <Grid size={RIGHT_COLUMN_WIDTH + 2.75}>{!pckg?.uid && <PackageSelect curatedOnly={true} />}</Grid>

      {pckg && (
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Grid size={10}>
            <Typography variant="h3" data-test="package-name" sx={{ overflowWrap: 'anywhere' }}>
              {`${t('Curated Package')}: ${selectedPackageState?.name ?? ''}`}
            </Typography>
          </Grid>

          <Grid
            size={2}
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            {(pckgUseAs === 'targeting' || pckgUseAs === 'templateReadOnly') && (
              <Button
                data-test="edit-package-button"
                disabled={isPackageEditDisabled || formMethods.formState.isSubmitting}
                onClick={onPackageEdit}
                variant="text"
                color="primary"
              >
                {t('Edit')}
              </Button>
            )}

            <Button
              sx={{ color: theme => theme.palette.error.main, marginLeft: theme => theme.spacing(2) }}
              data-test="remove-package-button"
              disabled={formMethods.formState.isSubmitting}
              onClick={onPackageRemove}
              variant="text"
              color="secondary"
            >
              {t('Remove')}
            </Button>
          </Grid>
        </Grid>
      )}

      {isDsp && pckgUseAs === 'templateReadOnly' && selectedPackageState && pckg?.uid && (
        <SelectedPackageDetails packageState={selectedPackageState} />
      )}

      {pckgUseAs === 'targeting' && selectedPackageState && pckg?.uid && (
        <SelectedPackageDetails packageState={selectedPackageState} />
      )}

      {pckgUseAs === 'template' && pckg?.uid && (
        <Fragment key={JSON.stringify(initialValues)}>
          <RHFNestedFormProvider<DealFormDataWithTargeting, UnionOfSectionNames>
            namespace="targeting"
            sections={{
              Targeting: {
                contentOnly: true,
              },
            }}
            formConfig={{
              noActions: true,
              noWrapper: true,
            }}
          >
            <PackageForm
              initialValues={{ ...initialValues, account_uid: observedAccountUid }}
              isSubmitting={formMethods.formState.isSubmitting}
              currency={currency}
              accountUid={observedAccountUid}
            />
          </RHFNestedFormProvider>
        </Fragment>
      )}
    </>
  );

  return <WhenLoaded loading={pckgIsLoading}>{render()}</WhenLoaded>;
};
